import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchData,
  sendEvent,
  invalidateCouponRedeemQueries,
} from "../../Helpers";
import DOMPurify from "dompurify";
import OutroTemplate from "./OutroTemplate";
import {
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  DETAILS_CACHE_TIME,
  getUserDetails,
} from "../../api/consumer";
import {
  ACTIVATION_CACHE_TIME,
  getActivationDescribe,
} from "../../api/activation";
import { getPackScanCount, packStats } from "../../api/pack";

import Quiz from "./Activities/Mechanics/Quiz";
import Contest from "./Activities/Mechanics/Contest";
import Poll from "./Activities/Mechanics/Poll";
import PollSlider from "./Activities/Mechanics/PollSlider";
import Puzzle from "./Activities/Mechanics/Puzzle";
import Meet from "./Activities/Mechanics/Meet";
import WheelOfFortune from "./Activities/Mechanics/WheelOfFortune";
import Scratch from "./Activities/Mechanics/Scratch";
import Spotlight from "./Activities/Mechanics/Spotlight";
import Calendar2024 from "./Activities/Mechanics/Calendar2024";
import Textarea from "./Activities/Mechanics/Textarea";
// import FashionWeek from "./Activities/Mechanics/FashionWeek";
import VuseUltra3D from "./Activities/Mechanics/VuseUltra3D";
import Maze from "./Activities/Mechanics/Maze";
import NiceModal from "@ebay/nice-modal-react";
import Auction from "./Activities/Mechanics/Auction";
import MemoryMatch from "./Activities/Mechanics/MemoryMatch";
import Match from "./Activities/Mechanics/Match";
import TermsAndConditions from "./Activities/Mechanics/TermsAndConditions";
import AnswerPatternContent from "./Activities/Mechanics/AnswerPatternContent";
import DominantAnswerContent from "./Activities/Mechanics/DominantAnswerContent";
import ScoreRangePersonaContent from "./Activities/Mechanics/ScoreRangePersonaContent";
import Freeze from "./Activities/Mechanics/Freeze";
import QuizVideo from "./Activities/Mechanics/QuizVideo";
import Results from "./Activities/Mechanics/Results";
import ConsumentPoll from "./Activities/Mechanics/ConsumentPoll";
import PickColor from "./Activities/Mechanics/PickColor";
import RevealImageQuiz from "./Activities/Mechanics/RevealImageQuiz";
import QuizInfo from "./Activities/Mechanics/QuizInfo";
import DragNDrop from "./Activities/Mechanics/DragNDrop";
import RepeatSequence from "./Activities/Mechanics/RepeatSequence";
import Find from "./Activities/Mechanics/Find";
import UserDataForm from "./Activities/Mechanics/UserDataForm";
import DjConsole from "./Activities/Mechanics/DjConsole";
import SetOrder from "./Activities/Mechanics/SetOrder";

export default function GameProvider() {
  const { ident } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isStatusSuccess = searchParams.get("status") === "success";
  const [contestAnswerSended, setContestAnswerSended] = useState(false);
  const [userPayload, setUserPayload] = useState(false);

  const { data: gameQuestionsQuery } = useQuery({
    queryKey: ["game-questions", ident],
    queryFn: async () => {
      let data;
      try {
        data = await import(`./Activities/Idents/${ident}/data.js`);
        return data.gameData;
      } catch (e) {
        console.error(e);
        throw new Error("Activation doesn't exist");
      }
    },
    staleTime: 1000 * 60 * 60,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  if (ident === "birthdayglo_2025") {
    //// nie pytaj czemu birthdayglo_2025
    const activationQuestionsQuery = useQuery({
      queryKey: ["game-questions", ident],
      queryFn: async () => {
        let data;
        try {
          data = await import(`./Activities/Idents/${ident}/data.js`);
          return data.gameData;
        } catch (e) {
          console.error(e);
          throw new Error("Activation doesn't exist");
        }
      },
      staleTime: 1000 * 60 * 60,
      cacheTime: ACTIVATION_CACHE_TIME,
    });
  }

  const detailsQuery = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    enabled: !!localStorage.getItem("token"),
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const isBirthdayglo2025 = ident === "birthdayglo_2025";

  const {
    data,
    isError,
    isLoading,
    refetch: refetchActivation,
  } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      isBirthdayglo2025
        ? () => {}
        : getActivationDescribe({
            activation: {
              ident: ident,
              withSolutions: true,
            },
          }),
    refetchOnMount: true,
    retry: false,
    staleTime: Infinity,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const requiredPacks = Number(gameQuestionsQuery?.requiredPacks) || 0;
  const { data: packStatsData, isLoading: packStatsLoading, isFetched: packStatsFetched } =
    useQuery({
      queryKey: ["pack-stats"],
      queryFn: async () => (await packStats()) ?? null,
      enabled: requiredPacks > 0,
      suspense: false,
      retry: false,
      refetchOnMount: true,
    });

  const isContest =
    Boolean(gameQuestionsQuery?.contest) || data?.type === "Contest";
  const returnsToContestIntro = isContest && gameQuestionsQuery?.showOutro !== true;

  async function sendAnswers(
    customSuccessCallback = false,
    argPayload = false,
  ) {
    const sendContestAnswer = Boolean(gameQuestionsQuery?.contestAnswer);
    fetchData({
      endpoint: sendContestAnswer
        ? "/activation/contest/answer"
        : "/activation/solution/create",
      payload: sendContestAnswer
        ? {
            contest: {
              ident: ident,
              content: {
                answers: argPayload ? argPayload : answers,
              },
            },
          }
        : !!gameQuestionsQuery?.transformPayload
          ? gameQuestionsQuery.transformPayload({
              activation: {
                ident: DOMPurify.sanitize(ident),
                payload: argPayload ? argPayload : answers,
              },
            })
          : {
              activation: {
                ident: DOMPurify.sanitize(ident),
                payload: argPayload ? argPayload : answers,
              },
            },
      successCallback: (resObj) => {
        setUserPayload(resObj?.data);
        detailsQuery.refetch();
        invalidateCouponRedeemQueries(queryClient);
        sendEvent(`${ident}_FINISH`);
        if (returnsToContestIntro) {
          queryClient.invalidateQueries({
            queryKey: ["activation-describe", ident],
          });
          navigate(`/contest/${ident}`, { replace: true });
        }
        if (ident === "quiz_13") {
          NiceModal.show("quiz-13-modal");
        }
        if (customSuccessCallback) {
          customSuccessCallback();
        }
      },
    });
  }

  async function sendContestSubmission(
    payload,
    { files = [], onSuccess, onError } = {},
  ) {
    const form = files.length ? new FormData() : null;

    if (form) {
      form.append("_input", JSON.stringify(payload));
      files.forEach((file) => form.append("files[]", file));
    }

    fetchData({
      endpoint: "/activation/contest/answer",
      ...(form ? { form } : { payload }),
      successCallback: (resObj) => {
        setUserPayload(resObj?.data);
        detailsQuery.refetch();
        invalidateCouponRedeemQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: ["activation-describe", ident],
        });
        sendEvent(`${ident}_FINISH`);
        if (returnsToContestIntro) {
          navigate(`/contest/${ident}`, { replace: true });
        } else {
          setContestAnswerSended(true);
        }
        if (onSuccess) onSuccess(resObj);
      },
      errorCallback: () => {
        if (onError) onError();
      },
    });
  }

  useEffect(() => {
    if (gameQuestionsQuery?.globalMechanics) {
      return;
    }

    const isPollQuestionExist = gameQuestionsQuery?.content.some(
      (question) => question?.mechanics === "Poll",
    );
    const lastMechanics =
      gameQuestionsQuery?.content[gameQuestionsQuery?.content.length - 1]
        ?.mechanics;
    const isHandleSendAnswer =
      gameQuestionsQuery?.content.length > 0 &&
      (lastMechanics === "Quiz" || lastMechanics === "PollSlider");

    const isManualSendAnswer = gameQuestionsQuery?.manualSendAnswer
      ? true
      : false;

    if (
      gameQuestionsQuery?.content &&
      !isPollQuestionExist &&
      !isHandleSendAnswer &&
      !isManualSendAnswer &&
      Object.keys(gameQuestionsQuery?.content).length === answers.length &&
      ident !== "memory_5"
    ) {
      console.log("AUTOSEND");
      console.log(answers);
      sendAnswers();
    }

    if (ident === "memory_5" && answers.length === 7) {
      console.log(answers);
      sendAnswers();
    }
  }, [gameQuestionsQuery, answers]);

  if (returnsToContestIntro && data?.completed) {
    return <Navigate to={`/contest/${ident}`} replace />;
  }

  if (requiredPacks > 0) {
    if (!packStatsFetched || packStatsLoading) {
      return null;
    }

    const scans = getPackScanCount(packStatsData);
    if (scans < requiredPacks || data?.completed) {
      return <Navigate to={`/contest/${ident}`} replace />;
    }
  }

  const contestFinished =
    returnsToContestIntro &&
    (gameQuestionsQuery?.content.length <= currentQuestion ||
      isStatusSuccess ||
      contestAnswerSended);

  if (contestFinished) {
    return null;
  }

  if (
    gameQuestionsQuery?.content.length <= currentQuestion ||
    isStatusSuccess ||
    contestAnswerSended
  ) {
    return (
      <OutroTemplate
        userPayload={userPayload}
        data={data}
        gameQuestionsQuery={gameQuestionsQuery}
      />
    );
  }

  console.log(gameQuestionsQuery);
  console.log(answers);

  return (
    <>
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics === "Quiz" && (
        <Quiz
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Contest" && (
        <Contest
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          answers={answers}
          sendContestSubmission={sendContestSubmission}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "QuizInfo" && (
        <QuizInfo
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics === "Poll" && (
        <Poll
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
          data={data}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "PollSlider" && (
        <PollSlider
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Textarea" && (
        <Textarea
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Puzzle" && (
        <Puzzle
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics === "Meet" && (
        <Meet
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Scratch" && (
        <Scratch
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Spotlight" && (
        <Spotlight
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {/* {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "FashionWeek" && (
        <FashionWeek
          data={data}
          detailsQuery={detailsQuery}
          refetchActivation={refetchActivation}
          setContestAnswerSended={setContestAnswerSended}
        />
      )} */}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "VuseUltra3D" && (
        <VuseUltra3D
          data={data}
          detailsQuery={detailsQuery}
          refetchActivation={refetchActivation}
          setContestAnswerSended={setContestAnswerSended}
          setUserPayload={setUserPayload}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics === "Maze" && (
        <Maze
          data={data}
          detailsQuery={detailsQuery}
          refetchActivation={refetchActivation}
          setContestAnswerSended={setContestAnswerSended}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Wheel" && <WheelOfFortune currentQuestion={currentQuestion} />}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Calendar2024" && (
        <Calendar2024
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Auction" && (
        <Auction
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "MemoryMatch" && (
        <MemoryMatch
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Match" && (
        <Match
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "AnswerPatternContent" && (
        <AnswerPatternContent
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "DominantAnswerContent" && (
        <DominantAnswerContent
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "ScoreRangePersonaContent" && (
        <ScoreRangePersonaContent
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "TermsAndConditions" && (
        <TermsAndConditions
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Freeze" && (
        <Freeze
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "QuizVideo" && (
        <QuizVideo
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "Results" && (
        <Results
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "ConsumentPoll" && (
        <ConsumentPoll
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
          setUserPayload={setUserPayload}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "PickColor" && (
        <PickColor
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "RevealImageQuiz" && (
        <RevealImageQuiz
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "DragNDrop" && (
        <DragNDrop
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "RepeatSequence" && (
        <RepeatSequence
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics === "Find" && (
        <Find
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "UserDataForm" && (
        <UserDataForm
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "DjConsole" && (
        <DjConsole
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
      {gameQuestionsQuery?.content?.[currentQuestion]?.mechanics ===
        "SetOrder" && (
        <SetOrder
          setCurrentQuestion={setCurrentQuestion}
          currentQuestion={currentQuestion}
          setAnswers={setAnswers}
          answers={answers}
          sendAnswers={sendAnswers}
        />
      )}
    </>
  );
}
